<template>
  <div class="news-item-block">
    <div class="row-3-custom p-top-9 p-bot-7">
      <div class="news-item-block-container">
        <NuxtLink
          :to="
            localePath({
              name: 'news',
            })
          "
        >
          <div class="text-medium5 default-icon-btn is-medium-bold is-reversed">
            <ArrowIcon alt="Arrow icon" class="icon-arrow" />
            <span v-html="$t('common.indietro')" />
          </div>
        </NuxtLink>
        <div class="news-item-block__intro">
          <div
            v-if="item.sezioneContenutoPagina.length"
            class="news-item-block__intro__reading-time text-small3"
          >
            <ClockIcon /> <span>{{ readingTime }}</span>
          </div>
          <h1 class="title-medium3 is-medium-bold" v-html="item.title" />
        </div>
        <div v-for="(item, index) in item.sezioneContenutoPagina" :key="index">
          <div v-if="item.__typename === 'immagine_Entry'">
            <div
              :class="['news-item-block__image', { first: index === 0 }]"
              :style="index === 0 ? { aspectRatio: firstImageAspectRatio } : {}"
            >
              <WidgetPicture
                :image="{
                  custom: item.custom?.[0],
                }"
                :alt="item.custom?.[0]?.title"
              />
            </div>
          </div>
          <div v-else-if="item.__typename === 'testo_Entry'">
            <div class="news-item-block__text text-editorial">
              <div class="text-medium5" v-html="item.text" />
            </div>
          </div>
        </div>
        <div
          v-if="item.sezioneContenutoPagina.length"
          class="news-item-block__text"
        >
          <div class="news-item-block__text__info">
            <div>
              <p class="text-small3" v-if="item.dataPubblicazione">
                {{ $t('common.published') }} {{ formattedDate }}
              </p>
            </div>
            <div class="news-item-block__text__info__socials">
              <a :href="facebookShareUrl" target="_blank"><FacebookIcon /></a>
              <a :href="linkedinShareUrl" target="_blank"><LinkedinIcon /></a>
              <a :href="twitterShareUrl" target="_blank"><TwitterIcon /></a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="item.notizieCorrelate.length" class="news-item-block__related">
      <CenteredBlocksList
        :news="item.notizieCorrelate"
        :title="$t('common.notizieCorrelate')"
      />
    </div>
  </div>
</template>

<script setup>
import ArrowIcon from '@/assets/img/ico/arrow-mobile.svg'
import TwitterIcon from '@/assets/img/ico/share-x.svg'
import FacebookIcon from '@/assets/img/ico/share-facebook.svg'
import LinkedinIcon from '@/assets/img/ico/share-linkedin.svg'
import ClockIcon from '@/assets/img/ico/clock-icon.svg'

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
})

const url = ref('')
const title = ref('')
const readingTime = ref(0)

onMounted(() => {
  if (props.item.sezioneContenutoPagina) {
    const textContent = props.item.sezioneContenutoPagina
      .filter((section) => section.__typename === 'testo_Entry')
      .map((section) => section.text)
      .join(' ')
    readingTime.value = calculateReadingTime(textContent)
    if (readingTime.value === 0) {
      readingTime.value = 1
    }
  }

  url.value = window.location.href
  title.value = document.title
})

const facebookShareUrl = computed(
  () =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url.value)}`,
)
const twitterShareUrl = computed(
  () =>
    `https://x.com/intent/tweet?text=${encodeURIComponent(url.value)}&text=${encodeURIComponent(title.value)}`,
)
const linkedinShareUrl = computed(
  () =>
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url.value)}&title=${encodeURIComponent(title.value)}`,
)

const formattedDate = computed(() => {
  const date = new Date(props.item.dataPubblicazione)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
})

const calculateReadingTime = (text) => {
  const wordsPerMinute = 200
  const words = text.match(/\b\w+\b/g)?.length || 0
  const totalMinutes = words / wordsPerMinute
  const totalSeconds = Math.ceil(totalMinutes * 60)
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
  const seconds = String(totalSeconds % 60).padStart(2, '0')

  return `${minutes}:${seconds}`
}

const firstImage = computed(() => {
  if (!props.item.sezioneContenutoPagina) return null
  const imgEntry = props.item.sezioneContenutoPagina.find(
    (section) => section.__typename === 'immagine_Entry',
  )
  return imgEntry?.custom?.[0] || null
})

// Ratio
const DEFAULT_RATIO = 896 / 380

// Calcola la ratio della prima immagine
// Se la ratio dell'immagine è simile a quella di default (tolleranza < 0.5), usa il default ratio
// Altrimenti restituisce una ratio custom
const firstImageAspectRatio = computed(() => {
  const ratio = firstImage.value.width / firstImage.value.height
  if (Math.abs(ratio - DEFAULT_RATIO) < 0.5) {
    return `${DEFAULT_RATIO} / 1`
  }
  return 1.4
})
</script>

<script>
export default {
  name: 'NewsItem',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
